import { useEffect, useRef } from "preact/hooks";

const READ_PERCENT = 80;
const READ_SECONDS = 180;

/** How far down to `selector`'s element the window is scrolled, in percent (100 when its top reaches the window's bottom). */
const scrollPercent = (selector: string): number => {
  const target = document.querySelector(selector);
  const range = target ? target.getBoundingClientRect().top + window.scrollY - window.innerHeight : document.documentElement.scrollHeight - window.innerHeight;
  if (range <= 0) {
    return 100;
  }
  return Math.min(100, Math.max(0, (window.scrollY / range) * 100));
};

/**
 * Runs `onRead` once the visitor has scrolled 80 % of the way down to `selector` and spent three minutes with the page
 * visible. Only while `enabled`: nothing is measured or sent for a signed-out visitor.
 */
export const useAutoRead = (key: string, selector: string, enabled: boolean, onRead: () => void): void => {
  const read = useRef(onRead);
  read.current = onRead;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let seconds = 0;
    let done = false;
    const check = () => {
      if (!done && seconds >= READ_SECONDS && scrollPercent(selector) >= READ_PERCENT) {
        done = true;
        read.current();
      }
    };
    const interval = setInterval(() => {
      if (document.visibilityState !== "hidden") {
        seconds += 1;
        check();
      }
    }, 1000);
    window.addEventListener("scroll", check, { passive: true });
    return () => {
      clearInterval(interval);
      window.removeEventListener("scroll", check);
    };
  }, [key, selector, enabled]);
};
